import React, { useState, useEffect, useCallback } from 'react';
import { muralAPI } from '../services/api';

const TEMPLATE_TYPES = [
  { value: 'heart', label: 'Corazón', icon: '❤️', description: 'Forma de corazón' },
  { value: 'star', label: 'Estrella', icon: '⭐', description: 'Estrella de 5 puntas' },
  { value: 'circle', label: 'Círculo', icon: '⭕', description: 'Círculos concéntricos' }
];

const MuralAdmin = ({ project }) => {
  const projectId = project?.id;
  const projectSlug = project?.slug;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({
    templateType: 'circle',
    isActive: false
  });
  const [message, setMessage] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!projectSlug) return;
    
    try {
      const muralResponse = await muralAPI.getConfig(projectSlug);
      
      if (muralResponse.data.success) {
        setConfig({
          templateType: muralResponse.data.config.templateType,
          isActive: muralResponse.data.config.isActive
        });
      }
    } catch (err) {
      console.error('Error cargando configuración:', err);
      setError('Error al cargar la configuración del mural');
    } finally {
      setLoading(false);
    }
  }, [projectSlug]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleTemplateChange = (templateType) => {
    setConfig(prev => ({ ...prev, templateType }));
  };

  const handleToggleActive = () => {
    setConfig(prev => ({ ...prev, isActive: !prev.isActive }));
  };

  const handleSave = async () => {
    if (!projectSlug) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await muralAPI.updateConfig(projectSlug, {
        templateType: config.templateType,
        isActive: config.isActive
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Configuración guardada correctamente' });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Error al guardar' });
      }
    } catch (err) {
      console.error('Error guardando configuración:', err);
      setMessage({ type: 'error', text: 'Error al guardar la configuración' });
    } finally {
      setSaving(false);
    }
  };

  const openMural = () => {
    if (projectSlug) {
      window.open(`/photobooth/${projectSlug}/mural`, '_blank');
    }
  };

  const openDisplay = () => {
    if (projectSlug) {
      window.open(`/photobooth/${projectSlug}/tv`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Configuración del Mural</h2>
        <p className="text-gray-600">Personaliza el mural fotográfico de tu evento</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Estado del Mural</h3>
            <p className="text-sm text-gray-500">Activa o desactiva la visualización del mural</p>
          </div>
          <button
            onClick={handleToggleActive}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              config.isActive ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              config.isActive ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        <div>
          <h3 className="font-medium text-gray-900 mb-3">Template del Mural</h3>
          <div className="grid grid-cols-3 gap-4">
            {TEMPLATE_TYPES.map(template => (
              <button
                key={template.value}
                onClick={() => handleTemplateChange(template.value)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.templateType === template.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-3xl mb-2">{template.icon}</div>
                <div className="font-medium text-gray-900">{template.label}</div>
                <div className="text-xs text-gray-500">{template.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
          
          <button
            onClick={openMural}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            Ver Mural
          </button>
          
          <button
            onClick={openDisplay}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            Ver Display
          </button>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">ℹ️ Información</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• El mural muestra las fotos organizadas en el template seleccionado</li>
          <li>• Solo las fotos no baneadas aparecen en el mural</li>
          <li>• Las actualizaciones son en tiempo real</li>
          <li>• Usa "Ver Mural" para ver la versión fullscreen</li>
        </ul>
      </div>
    </div>
  );
};

export default MuralAdmin;